import { Box, Typography } from "@mui/material";
import { DatePicker, type DatePickerProps } from "@mui/x-date-pickers";
import { formatDateToDateString } from "Utils/DateUtils";
import {
  type Control,
  Controller,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

interface Props<FormType extends FieldValues, TransformedFieldValues = FormType>
  extends DatePickerProps {
  name: FieldPath<FormType>;
  label?: string;
  control: Control<FormType, unknown, TransformedFieldValues>;
}

export const DateFormInput = <T extends object, V extends object>({
  name,
  label,
  control,
  ...pickerProps
}: Props<T, V>) => (
  <Controller
    name={name}
    control={control}
    render={({ field: { onChange, value }, fieldState: { error } }) => (
      <Box display="flex" flexDirection="column" width="100%" minHeight="44px">
        {label && (
          <Typography
            component="label"
            htmlFor={name}
            sx={{ mb: 1, fontWeight: 400 }}
          >
            {label}
          </Typography>
        )}
        <DatePicker
          {...pickerProps}
          onChange={(value) => {
            const isInvalidDate = Number.isNaN(value?.getTime()); // for cases where some date segments are missing (eg. 11.mm.2025), we want to keep the value, but the date is invalid
            if (isInvalidDate) {
              return value;
            }
            onChange(value ? formatDateToDateString(value) : null);
          }}
          value={value ? new Date(value) : null}
          slotProps={{
            openPickerButton: { disableRipple: true },
            textField: {
              id: name,
              helperText: error ? error.message : " ",
              error: !!error,
            },
          }}
        />
      </Box>
    )}
  />
);
